import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Empty, SpinLoading } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError } from "../../components/Toast";
import { formatPrice } from "../../utils";

const walletOutImg = require("../../assets/images/wallet/walletOut.png");
const walletInImg = require("../../assets/images/wallet/walletIn.png");
const takeOrderAddImg = require("../../assets/images/wallet/takeOrderAdd.png");
const pendingOrderReturnImg = require("../../assets/images/wallet/pendingOrderReturn.png");
const pendingOrderDeductImg = require("../../assets/images/wallet/pendingOrderDeduct.png");
const transOfficialImg = require("../../assets/images/wallet/transOfficial.png");
const bgImg = require("../../assets/images/wallet/walletGroup.png");

enum BALANCE_TYPE {
  ALL = -1,
  WALLET_IN = 0,
  WALLET_OUT = 1,
  TAKE_ORDER_ADD = 4,
  PENDING_ORDER_DEDUCT = 5,
  TAKE_ORDER_CANCEL = 6,
  PENDING_ORDER_RETURN = 7,
  TRANS_OFFICIAL = 8,
}

const TYPE_META: Record<number, { label: string; icon: any }> = {
  [BALANCE_TYPE.WALLET_IN]: { label: "转入", icon: walletInImg },
  [BALANCE_TYPE.WALLET_OUT]: { label: "转出", icon: walletOutImg },
  [BALANCE_TYPE.TAKE_ORDER_ADD]: { label: "买入", icon: takeOrderAddImg },
  [BALANCE_TYPE.PENDING_ORDER_DEDUCT]: { label: "挂单扣款", icon: pendingOrderDeductImg },
  [BALANCE_TYPE.TAKE_ORDER_CANCEL]: { label: "订单取消", icon: pendingOrderReturnImg },
  [BALANCE_TYPE.PENDING_ORDER_RETURN]: { label: "挂单退回", icon: pendingOrderReturnImg },
  [BALANCE_TYPE.TRANS_OFFICIAL]: { label: "官方转账", icon: transOfficialImg },
};

const Body = styled.div`
  height: calc(100% - ${headerHeight});
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f7f8ff;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: #fff;
  padding-bottom: 12px;
`;

const Hero = styled.div`
  flex-shrink: 0;
  margin: 12px;
  border-radius: 10px;
  padding: 16px;
  color: #fff;
  background: url(${bgImg}) center/cover no-repeat, linear-gradient(135deg, #1c9cea, #4db3ff);
`;

const Tabs = styled.div`
  flex-shrink: 0;
  display: flex;
  overflow-x: auto;
  background: #fff;
  border-bottom: 1px solid #eee;
`;

const Tab = styled.div<{ $active?: boolean }>`
  flex: 0 0 auto;
  padding: 12px 14px;
  font-size: 13px;
  cursor: pointer;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : "#999"};
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : "transparent")};
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 12px;
  padding: 12px;
  background: #f7f8ff;
  border-radius: 8px;
  cursor: pointer;
`;

const tabs = [
  { key: BALANCE_TYPE.ALL, label: "全部" },
  { key: BALANCE_TYPE.WALLET_IN, label: "转入" },
  { key: BALANCE_TYPE.WALLET_OUT, label: "转出" },
  { key: BALANCE_TYPE.TAKE_ORDER_ADD, label: "买入" },
  { key: BALANCE_TYPE.PENDING_ORDER_DEDUCT, label: "挂单" },
  { key: BALANCE_TYPE.TRANS_OFFICIAL, label: "官方" },
];

const WalletLog = () => {
  const { navigate } = useNavigation();
  const { walletStore } = useStores();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<any[]>([]);
  const [type, setType] = useState(BALANCE_TYPE.ALL);

  const load = async () => {
    try {
      setLoading(true);
      const params: any = { current: 1, pageSize: 30 };
      if (type !== BALANCE_TYPE.ALL) params.balance_type = type;
      const res = await walletStore.getWalletLog(params);
      setList(Array.isArray(res?.data) ? res.data : res?.data?.items || []);
    } catch (e: any) {
      ToastError(e.message || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    walletStore.getWalletInfo();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <PageWrap>
      <PageHeader title="钱包详情" />
      <Body>
        <Hero>
          <div style={{ opacity: 0.9 }}>喜鹊币</div>
          <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6 }}>
            {formatPrice(walletStore.balance || 0)}
          </div>
          <div style={{ opacity: 0.9, marginTop: 10 }}>
            ≈ ¥ {formatPrice(walletStore.balance || 0)} CNY
          </div>
        </Hero>
        <Tabs>
          {tabs.map((t) => (
            <Tab key={t.key} $active={type === t.key} onClick={() => setType(t.key)}>
              {t.label}
            </Tab>
          ))}
        </Tabs>
        <ScrollArea>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <SpinLoading />
            </div>
          ) : list.length === 0 ? (
            <Empty description="暂无流水" style={{ padding: 40 }} />
          ) : (
            list.map((item, idx) => {
              const meta = TYPE_META[item.balance_type] || {
                label: item.note || "流水",
                icon: walletInImg,
              };
              return (
                <Item
                  key={item.id || idx}
                  onClick={() =>
                    navigate("WalletLogDetail", { id: item.id, walletLog: item })
                  }
                >
                  <img src={meta.icon} alt="" width={40} height={40} />
                  <div style={{ flex: 1, marginLeft: 10 }}>
                    <div style={{ fontWeight: 600 }}>{meta.label}</div>
                    <div style={{ color: "#999", fontSize: 12 }}>
                      {item.created || item.op_at || ""}
                    </div>
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: Number(item.amount) >= 0 ? "#33c341" : "#df2121",
                    }}
                  >
                    {Number(item.amount) >= 0 ? "+" : ""}
                    {formatPrice(item.amount || 0)}
                  </div>
                </Item>
              );
            })
          )}
        </ScrollArea>
      </Body>
    </PageWrap>
  );
};

export default observer(WalletLog);
